import React, { memo } from 'react';
import { Expand, Shrink, Menu, ExternalLink } from 'lucide-react';

const isIOSDevice =
  /iphone|ipod/i.test(navigator.userAgent);
import interactionsIcon from '../assets/icons/homepage/interactions.svg';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import {
  HotspotSwitch,
  useGetConfiguration,
  Background,
  FreeRoamSwitch,
  useAccessoryStore,
  SetActiveAccessory,
} from '@adloid-technologies/discover-sdk';
import { useLoaderState } from '@adloid-technologies/discover-sdk/dist/esm/store/loader-store';
import { useTranslation, useRTL } from '../utils/translations';
import { getViewportLoaderText } from '../utils/viewportLoaderText';
import { InteractionsPanel } from './InteractionsPanel/InteractionsPanel';
import { assetSrc, assetUrl, uimetadata } from '../utils/metadata';
import { useLogEvent, AnalyticsIds } from '../utils/useAnalytics';

interface ViewportProps {
  isPreviewMode: boolean;
  isDarkMode: boolean;
  isLoading?: boolean;
  activeTab?: string;
  onChangeBackground?: () => void;
  onShare?: () => void;
  onExploreFeatures?: () => void;
  onBuildAndPrice?: () => void;
}

// FreeRoam/Featured View toggle button — `selected` and `id` are injected by FreeRoamSwitch via cloneElement
interface FreeRoamButtonProps {
  selected?: boolean;
  id?: 'free-roam' | 'feature-view' | null;
  isDarkMode?: boolean;
  isMobile?: boolean;
}

const FreeRoamButton = memo(
  React.forwardRef<HTMLDivElement, FreeRoamButtonProps>(
    ({ id, isDarkMode = true, isMobile = false }, ref) => {
      const { t } = useTranslation();
      return (
        <div
          ref={ref}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            background: isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255,255,255,0.85)',
            borderRadius: 'var(--brand-pill-radius)',
            padding: '8px 16px',
            color: isDarkMode ? '#fff' : '#111',
            cursor: 'pointer',
            fontSize: '14px',
            backdropFilter: 'blur(5px)',
            border: isDarkMode ? 'none' : '1px solid rgba(0,0,0,0.1)',
            width: isMobile ? '160px' : '182px',
            minWidth: isMobile ? '160px' : '182px',
            height: '35px',
            overflow: 'hidden',
            userSelect: 'none',
          }}
        >
          <img
            src={`${assetUrl}icons/homepage/switch.svg`}
            alt="Switch"
            style={{
              width: '16px',
              height: '16px',
              filter: isDarkMode ? 'brightness(0) invert(1)' : 'brightness(0)',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {id === 'free-roam'
              ? t('staticTranslations.bottomDrawer.freeRoamView', 'Free Roam View')
              : t('staticTranslations.bottomDrawer.featureView', 'Featured View')}
          </span>
        </div>
      );
    }
  )
);
FreeRoamButton.displayName = 'FreeRoamButton';

// Camera toggle button — `selected` and `id` are injected by HotspotSwitch via cloneElement
interface CameraButtonProps {
  label: string;
  selected?: boolean;
  id?: string;
  isDarkMode: boolean;
}

const CameraButton = React.forwardRef<
  HTMLButtonElement,
  CameraButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ label, selected = false, isDarkMode, id: _id, ...rest }, ref) => (
  <button
    ref={ref}
    {...rest}
    className={cn(
      'px-3 py-1 text-[12.934px] font-medium rounded-full transition-all uppercase',
      selected
        ? isDarkMode ? 'bg-white text-black shadow-lg' : 'bg-black text-white shadow-lg'
        : isDarkMode ? 'text-white opacity-60 hover:opacity-100' : 'text-gray-500 hover:text-black',
    )}
  >
    {label}
  </button>
));
CameraButton.displayName = 'CameraButton';

// Background card — pixel-perfect match of Figma 740:62
// Card: 168.875 × 114.625px, image 182 × 85.75px (overflows horizontally), label at bottom
interface BgCardProps {
  selected?: boolean;
  id?: string;
  title: string;
  image: string;
  isDarkMode?: boolean;
  isRTL?: boolean;
  compact?: boolean;
  mobile?: boolean;
}
const BgCard: React.FC<BgCardProps> = ({ selected = false, title, image, isDarkMode = true, isRTL = false, compact = false, mobile = false }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const cardW = mobile ? 94 : compact ? 133 : 168.875;
  const cardH = mobile ? 74 : compact ? 90 : 114.625;
  const imgW = mobile ? 118 : compact ? 143 : 182;
  const imgH = mobile ? 55.5 : compact ? 67.65 : 85.75;
  return (
    <div
      className={cn(
        'relative cursor-pointer overflow-hidden flex-shrink-0',
        compact ? 'rounded-[var(--brand-card-radius-sm)]' : 'rounded-[var(--brand-card-radius)]',
        isDarkMode ? 'bg-[#222]' : 'bg-[#e8eaed]',
      )}
      style={{
        width: `${cardW}px`,
        height: `${cardH}px`,
        border: selected
          ? '1px solid var(--brand-accent)'
          : isDarkMode ? '0.875px solid #555' : '0.875px solid #c8cdd5',
      }}
    >
      {/* Image */}
      <div
        className="absolute overflow-hidden"
        style={{
          width: `${imgW}px`,
          height: `${imgH}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          top: 0,
        }}
      >
        <img
          src={assetSrc(image)}
          alt={title}
          className="absolute top-0 left-0 w-full"
          style={{ height: '106.12%' }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      </div>
      {/* Label */}
      <p
        className={cn(
          'absolute whitespace-nowrap font-bold leading-normal',
          isDarkMode ? 'text-white' : 'text-gray-900',
        )}
        style={{
          bottom: mobile ? '2px' : compact ? '5px' : '6px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: mobile ? '8px' : compact ? '9.5px' : '12.25px',
          fontFamily: "var(--default-font-family), sans-serif",
        }}
      >
        {title}
      </p>
      {/* Tooltip — shown when info badge is hovered */}
      {showTooltip && (
        <div
          className={cn(
            'absolute z-20 px-2 py-1 rounded-[var(--brand-card-radius-sm)] pointer-events-none',
            isDarkMode ? 'bg-white text-black' : 'bg-gray-900 text-white',
          )}
          style={{
            top: '24px',
            right: '6px',
            maxWidth: '140px',
            fontSize: '10px',
            lineHeight: '1.3',
            wordBreak: 'break-word',
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left',
          }}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {title}
        </div>
      )}
      {/* Info badge — white circle with black "i" */}
      <div
        className="absolute rounded-full bg-white flex items-center justify-center cursor-pointer z-10"
        style={{
          width: mobile ? '8.485px' : compact ? '10.5px' : '13.125px',
          height: mobile ? '8.485px' : compact ? '10.5px' : '13.125px',
          top: mobile ? '3.18px' : '5px',
          right: mobile ? '3.18px' : compact ? '5px' : undefined,
          left: mobile ? undefined : compact ? undefined : '146.13px',
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className="font-bold text-black leading-none select-none"
          style={{ fontSize: mobile ? '6px' : compact ? '7px' : '8.75px', fontFamily: "'Beausite Classic Trial', sans-serif" }}
        >
          i
        </span>
      </div>
    </div>
  );
};

export const Viewport: React.FC<ViewportProps> = ({
  isPreviewMode,
  isDarkMode,
  isLoading = false,
  activeTab,
  onChangeBackground,
  onShare,
  onExploreFeatures,
  onBuildAndPrice,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isBackgroundSelectorOpen, setIsBackgroundSelectorOpen] = React.useState(false);

  const { accessoryView, setAccessoryView } = useAccessoryStore();
  const [showFreeRoam, setShowFreeRoam] = React.useState(false);
  // Color swaps (esp. interior, which reloads assets) can take a while; show a light
  // overlay while the SDK reports the color as loading.
  const isColorLoading = useLoaderState((s) => s.isColorLoading);

  React.useEffect(() => {
    setShowFreeRoam(accessoryView !== null);
  }, [accessoryView]);

  const resetToFreeRoam = React.useCallback(() => {
    if (accessoryView === 'feature-view') {
      setAccessoryView('free-roam');
    }
    SetActiveAccessory(null);
  }, [accessoryView, setAccessoryView]);

  const toggleFullscreen = React.useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  React.useEffect(() => {
    const onFSChange = () => setIsExpanded(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFSChange);
    return () => document.removeEventListener('fullscreenchange', onFSChange);
  }, []);

  const [isInteractionsOpen, setIsInteractionsOpen] = React.useState(false);
  const interactionsBtnRef = React.useRef<HTMLDivElement>(null);
  const [isMobileInteractionsOpen, setIsMobileInteractionsOpen] = React.useState(false);
  const mobileInteractionsRef = React.useRef<HTMLDivElement>(null);
  const mobileInteractionsBtnRef = React.useRef<HTMLButtonElement>(null);

  // Dynamically position the interactions overlay above the actual button
  // (removed — overlay is now positioned via CSS inside the button's relative container)

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const mobileMenuRef = React.useRef<HTMLDivElement>(null);
  const mobileBgRef = React.useRef<HTMLDivElement>(null);
  const [isSeatDropdownOpen, setIsSeatDropdownOpen] = React.useState(false);
  const seatDropdownRef = React.useRef<HTMLDivElement>(null);
  const { view, backgroundId, variantID } = useGetConfiguration();
  const isInterior = view?.type === 'interior';
  const { t, currentLanguage, setLanguage } = useTranslation();
  const { isRTL } = useRTL();
  const { trackEvent } = useLogEvent();
  const prevBackgroundIdRef = React.useRef(backgroundId);

  // Interior seat hotspots from metadata
  const modelId2 = (uimetadata.config as any).model as string;
  const hotspots = ((uimetadata.models as any)[modelId2]?.hotspots ?? {}) as Record<string, { title: { en: string; ar: string } }>;
  // Hotspots are declared once per model, not per variant (all camera slots are
  // mandatory in the schema) - but e.g. a Regular Cab truck genuinely has no
  // second row to show. hiddenHotspots on the current variant (ui-metadata) lets
  // a variant opt out of a hotspot the model otherwise declares.
  const hiddenHotspotIds: string[] = (uimetadata.models as any)[modelId2]?.variants?.[variantID]?.hiddenHotspots ?? [];
  const interiorHotspotIds = Object.keys(hotspots).filter(id => id.includes('interior') && !hiddenHotspotIds.includes(id));
  const activeSeatId = isInterior ? (view?.hotspotId || interiorHotspotIds[0] || 'interiorCamera') : 'interiorCamera';

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
        setIsBackgroundSelectorOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (interactionsBtnRef.current && !interactionsBtnRef.current.contains(e.target as Node)) {
        setIsInteractionsOpen(false);
      }
    };
    if (isInteractionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isInteractionsOpen]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const inOverlay = mobileInteractionsRef.current?.contains(e.target as Node);
      const inButton = mobileInteractionsBtnRef.current?.contains(e.target as Node);
      if (!inOverlay && !inButton) {
        setIsMobileInteractionsOpen(false);
      }
    };
    if (isMobileInteractionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileInteractionsOpen]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const inMobileMenu = mobileMenuRef.current?.contains(e.target as Node);
      const inMobileBg = mobileBgRef.current?.contains(e.target as Node);
      if (!inMobileMenu && !inMobileBg) {
        setIsMobileMenuOpen(false);
        setIsBackgroundSelectorOpen(false);
      }
    };
    if (isMobileMenuOpen || isBackgroundSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen, isBackgroundSelectorOpen]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (seatDropdownRef.current && !seatDropdownRef.current.contains(e.target as Node)) {
        setIsSeatDropdownOpen(false);
      }
    };
    if (isSeatDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSeatDropdownOpen]);

  // Close all panels when the user clicks inside the iframe (focus leaves window)
  React.useEffect(() => {
    const handleWindowBlur = () => {
      if (document.activeElement?.tagName === 'IFRAME') {
        setIsMenuOpen(false);
        setIsMobileMenuOpen(false);
        setIsBackgroundSelectorOpen(false);
      }
    };
    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, []);

  // Collect backgrounds from metadata
  const modelId = (uimetadata.config as any).model as string;
  const backgrounds = ((uimetadata.models as any)[modelId]?.backgrounds ?? {}) as Record<string, { image: string; title: { en: string; ar: string } }>;

  const handleBackgroundClick = (_type: 'background', id: string) => {
    const prevId = prevBackgroundIdRef.current;
    trackEvent(AnalyticsIds.backgroundInteract, {
      primaryTypeName: backgrounds[id]?.title?.en || id,
      secondaryTypeName: prevId ? backgrounds[prevId]?.title?.en || prevId : '',
    });
    prevBackgroundIdRef.current = id;
  };

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className={cn(
        'relative flex-1 flex items-center justify-center min-h-[300px] md:min-h-0 transition-colors duration-300',
        isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50',
      )}
    >
      {/* Clipped layer: iframe + loading overlay stay within bounds */}
      <div className="absolute inset-0 overflow-hidden">
        {/* SDK 3D Viewer iframe — communicates with DiscoverProvider via postMessage */}
        {/* ph-no-capture: PostHog session replay blocks this element (and the
            3D canvas inside it), so the renderer is never recorded. */}
        <iframe
          id="gsdk"
          className="ph-no-capture"
          title="3D Car Viewer"
          src={`${assetUrl}gsdk-html/gsdk-dev.html#/viewer`}
          sandbox="allow-same-origin allow-scripts"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            background: 'transparent',
            zIndex: 0,
          }}
        />

        {/* Cinematic-type features (HandleFeaturesAutoplay in the SDK) just
            toggle this element's display and set its src, with no layout of
            its own - lives here, over the 3D viewer, so it plays in the same
            area as every other feature/interaction animation instead of
            covering the whole screen. An explicit, higher z-index than the
            iframe above is required, not just DOM order: hardware-accelerated
            iframe content is sometimes composited in its own layer that
            ignores normal stacking order with sibling elements, which shows
            as the iframe painting over the video (a black rect, since the
            iframe's own canvas background is black) even though the video is
            actually playing underneath. muted+playsInline avoid the video
            being silently blocked by the browser's autoplay policy.  */}
        <video
          id="features-video-element"
          muted
          playsInline
          className="absolute inset-0 z-30 h-full w-full object-contain bg-black"
          style={{ display: 'none' }}
        />

        {/* Loading overlay */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className={cn(
                'absolute inset-0 flex items-center justify-center z-20',
                isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50',
              )}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-[var(--brand-accent)] border-t-transparent rounded-full animate-spin" />
                <span
                  className={cn(
                    'text-[11px] font-medium uppercase tracking-wider',
                    isDarkMode ? 'text-white/40' : 'text-gray-400',
                  )}
                >
                  Loading 3D model…
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Color-change overlay — shown while a color (esp. interior) is loading */}
        <AnimatePresence>
          {isColorLoading && !isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cn(
                'absolute inset-0 flex items-center justify-center z-20 backdrop-blur-[2px]',
                isDarkMode ? 'bg-[#0a0a0a]/50' : 'bg-gray-50/50',
              )}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-[var(--brand-accent)] border-t-transparent rounded-full animate-spin" />
                <span
                  className={cn(
                    'text-[11px] font-medium uppercase tracking-wider',
                    isDarkMode ? 'text-white/60' : 'text-gray-500',
                  )}
                >
                  {getViewportLoaderText() ?? t('staticTranslations.viewport.applyingColor', 'Applying color…')}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Viewport Controls */}
      {!isPreviewMode && (
        <>
          {/* Interior Seat Row Dropdown — centered top, shown only in interior view */}
          <AnimatePresence>
            {isInterior && interiorHotspotIds.length > 1 && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="absolute top-5 z-[45] left-0 right-0 flex justify-center pointer-events-none"
                style={{ direction: 'ltr' }}
              >
              <div ref={seatDropdownRef} className="pointer-events-auto">
                {/* Trigger button */}
                <button
                  onClick={() => setIsSeatDropdownOpen((prev: boolean) => !prev)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-medium transition-all backdrop-blur-md border whitespace-nowrap',
                    isDarkMode
                      ? 'bg-black/50 border-white/15 text-white hover:bg-black/70'
                      : 'bg-white/85 border-black/10 text-black hover:bg-white',
                  )}
                >
                  <span>
                    {isRTL
                      ? (hotspots[activeSeatId]?.title?.ar ?? hotspots[activeSeatId]?.title?.en ?? activeSeatId)
                      : (hotspots[activeSeatId]?.title?.en ?? activeSeatId)}
                  </span>
                  <svg
                    width="10" height="7" viewBox="0 0 12 8" fill="none"
                    className={cn('transition-transform duration-200 flex-shrink-0', isSeatDropdownOpen && 'rotate-180')}
                  >
                    <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {/* Dropdown panel */}
                <AnimatePresence>
                  {isSeatDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -4 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      className={cn(
                        'absolute top-full mt-2 left-1/2 -translate-x-1/2 rounded-[var(--brand-card-radius)] border shadow-2xl overflow-hidden backdrop-blur-xl min-w-[180px]',
                        isDarkMode ? 'bg-[#141414] border-white/10' : 'bg-white border-gray-200',
                      )}
                    >
                      {interiorHotspotIds.map(id => (
                        <HotspotSwitch key={id} hotspotId={id as any}>
                          <button
                            onClick={() => setIsSeatDropdownOpen(false)}
                            className={cn(
                              'w-full px-4 py-3 text-[12px] transition-colors border-b last:border-b-0',
                              isRTL ? 'text-right' : 'text-left',
                              id === activeSeatId
                                ? 'text-[var(--brand-accent)] font-semibold'
                                : isDarkMode ? 'text-white/80 hover:bg-white/5' : 'text-gray-800 hover:bg-gray-50',
                              isDarkMode ? 'border-white/5' : 'border-gray-100',
                            )}
                          >
                            {isRTL
                              ? (hotspots[id]?.title?.ar ?? hotspots[id]?.title?.en ?? id)
                              : (hotspots[id]?.title?.en ?? id)}
                          </button>
                        </HotspotSwitch>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* FreeRoam / Featured View toggle — above mobile toolbar + desktop center */}
          {showFreeRoam && (
            <div className="absolute flex justify-center z-30 bottom-[57px] @3xl:bottom-6" style={{ left: '50%', transform: 'translateX(-50%)' }}>
              <FreeRoamSwitch>
                <FreeRoamButton isDarkMode={isDarkMode} />
              </FreeRoamSwitch>
            </div>
          )}

          {/* Desktop bottom controls */}
          <div className={cn("absolute bottom-6 hidden @3xl:flex items-center gap-4 z-30", isRTL ? "right-[34px]" : "left-6")}>
            {!isIOSDevice && (
            <div className="group flex flex-col items-center gap-1">
              <button
                onClick={() => { resetToFreeRoam(); toggleFullscreen(); }}
                className={cn(
                  'w-[53px] h-[53px] rounded-full flex items-center justify-center transition-all',
                  isExpanded 
                    ? "bg-[var(--brand-accent)] text-white" 
                    : (isDarkMode ? "bg-white/5 backdrop-blur-md border border-white/10 text-white hover:bg-white/10" : "bg-white border border-gray-200 text-gray-600 shadow-sm hover:bg-gray-50")
                )}
              >
                {isExpanded ? <Shrink className="w-6 h-6" /> : <Expand className="w-6 h-6" />}
              </button>
              <span className={cn(
                "text-[12px] font-medium uppercase tracking-wider transition-opacity opacity-0 group-hover:opacity-100",
                isDarkMode ? "text-white/40" : "text-gray-400"
              )}>{isExpanded ? t('staticTranslations.bottomDrawer.minimize', 'Minimize') : t('staticTranslations.bottomDrawer.expand', 'Expand')}</span>
            </div>
            )}

            {/* Middle: Interactions button + panel */}
            <div ref={interactionsBtnRef} className="relative">
              {/* Panel anchored above this wrapper */}
              <InteractionsPanel
                isOpen={isInteractionsOpen}
                onClose={() => setIsInteractionsOpen(false)}
                isDarkMode={isDarkMode}
              />

              <div className="group flex flex-col items-center gap-1">
                <button
                  onClick={() => { resetToFreeRoam(); setIsInteractionsOpen((p) => !p); }}
                  className={cn(
                    'w-[53px] h-[53px] rounded-full flex items-center justify-center transition-all',
                    isInteractionsOpen
                      ? 'bg-[var(--brand-accent)] text-white shadow-lg shadow-[var(--brand-accent)]/30'
                      : isDarkMode
                      ? 'bg-white/5 backdrop-blur-md border border-white/10 text-white hover:bg-white/10'
                      : 'bg-white border border-gray-200 text-gray-600 shadow-sm hover:bg-gray-50',
                  )}
                >
                  <img src={interactionsIcon} alt="interactions" className={cn('w-6 h-6', !isDarkMode && !isInteractionsOpen && 'invert')} />
                </button>
                <span className={cn(
                  'text-[12px] font-medium uppercase tracking-wider transition-opacity opacity-0 group-hover:opacity-100',
                  isDarkMode ? 'text-white/40' : 'text-gray-400',
                )}>
                  {t('staticTranslations.bottomDrawer.interactions', 'Interactions')}
                </span>
              </div>
            </div>

            <div ref={menuRef} className="relative">
              <div className="group flex flex-col items-center gap-1">
                <button
                  onClick={() => {
                    resetToFreeRoam();
                    trackEvent(AnalyticsIds.menuInteract, {
                      state: !isMenuOpen,
                      primaryTypeName: 'trayCta',
                    });
                    if (isMenuOpen) { setIsMenuOpen(false); setIsBackgroundSelectorOpen(false); }
                    else { setIsMenuOpen(true); }
                  }}
                  className={cn(
                    "w-[53px] h-[53px] rounded-full flex items-center justify-center transition-all",
                    isMenuOpen
                      ? isDarkMode ? "bg-white text-black" : "bg-[var(--brand-accent)] text-white shadow-lg shadow-[var(--brand-accent)]/30"
                      : isDarkMode ? "bg-white/5 backdrop-blur-md border border-white/10 text-white hover:bg-white/10" : "bg-white border border-gray-200 text-gray-600 shadow-sm hover:bg-gray-50"
                  )}
                >
                  <Menu className="w-6 h-6" />
                </button>
                <span className={cn(
                  "text-[12px] font-medium uppercase tracking-wider transition-opacity opacity-0 group-hover:opacity-100",
                  isDarkMode ? "text-white/40" : "text-gray-400"
                )}>{t('staticTranslations.viewport.menu', 'Menu')}</span>
              </div>

              {/* Combined popup row: primary menu + background sub-panel */}
              <AnimatePresence>
                {isMenuOpen && (
                  <div
                    className={cn(
                      "absolute bottom-full mb-3 flex items-end gap-2 z-[100] flex-row",
                      isRTL ? "right-0" : "left-0"
                    )}
                  >
                    {/* Primary menu — always first in DOM; left anchor (LTR) or right anchor (RTL) */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.94, y: 6 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.94, y: 6 }}
                      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                      className={cn(
                        "rounded-[var(--brand-card-radius)] shadow-2xl p-[7px] flex-shrink-0",
                        isDarkMode ? "bg-[#141414] border border-[#4b4b4b]/50" : "bg-white border border-gray-200"
                      )}
                    >
                      <div className="flex flex-col gap-[5px]">
                        {/* Change Background */}
                        <button
                          onClick={() => {
                            trackEvent(AnalyticsIds.backgroundInteract, {
                              state: !isBackgroundSelectorOpen,
                              parent: 'menu',
                              primaryTypeName: 'background',
                            });
                            setIsBackgroundSelectorOpen((p) => !p);
                          }}
                          className={cn(
                            "flex items-center gap-[10px] whitespace-nowrap h-[28px] px-[10px] rounded-[var(--brand-button-radius)] border w-full transition-colors",
                            isDarkMode
                              ? isBackgroundSelectorOpen ? "border-[var(--brand-accent)]" : "border-white/20 hover:border-white/40"
                              : isBackgroundSelectorOpen ? "border-[var(--brand-accent)]" : "border-[#666]/40 hover:border-[#666]/70"
                          )}
                        >
                          <img
                            src={`${assetUrl}icons/homepage/mountain.svg`}
                            alt=""
                            className={cn("w-[18px] h-[11px] object-contain shrink-0", !isDarkMode && !isBackgroundSelectorOpen && "brightness-0")}
                            draggable={false}
                          />
                          <span className={cn(
                            "text-[11px] font-light leading-normal",
                            isBackgroundSelectorOpen ? "text-[var(--brand-accent)]" : (isDarkMode ? "text-white" : "text-gray-900")
                          )}>
                            {t('staticTranslations.viewport.changeBackground', 'Change Background')}
                          </span>
                        </button>

                        {/* Select Language */}
                        <button
                          onClick={() => {
                            const newLanguage = currentLanguage === 'en' ? 'ar' : 'en';
                            trackEvent(AnalyticsIds.languageInteract, {
                              parent: 'menu',
                              primaryTypeName: newLanguage,
                              secondaryTypeName: currentLanguage,
                            });
                            setLanguage(newLanguage);
                            setIsMenuOpen(false);
                            setIsBackgroundSelectorOpen(false);
                          }}
                          className={cn(
                            "flex items-center gap-[10px] whitespace-nowrap h-[28px] px-[10px] rounded-[var(--brand-button-radius)] border w-full transition-colors",
                            isDarkMode ? "border-white/20 hover:border-white/40" : "border-[#666]/40 hover:border-[#666]/70"
                          )}
                        >
                          <img
                            src={`${assetUrl}icons/homepage/translate.svg`}
                            alt=""
                            className={cn("w-[18px] h-[18px] object-contain shrink-0", !isDarkMode && "brightness-0")}
                            draggable={false}
                          />
                          <span className={cn("text-[11px] font-light leading-normal", isDarkMode ? "text-white" : "text-gray-900")}>
                            {t('staticTranslations.viewport.selectLanguage', 'Switch Language')}
                          </span>
                        </button>

                        {/* Share */}
                        <button
                          onClick={() => { setIsMenuOpen(false); setIsBackgroundSelectorOpen(false); onShare?.(); }}
                          className={cn(
                            "flex items-center gap-[10px] whitespace-nowrap h-[28px] px-[10px] rounded-[var(--brand-button-radius)] border w-full transition-colors",
                            isDarkMode ? "border-white/20 hover:border-white/40" : "border-[#666]/40 hover:border-[#666]/70"
                          )}
                        >
                          <img
                            src={`${assetUrl}icons/homepage/share.svg`}
                            alt=""
                            className={cn("w-[13px] h-[16px] object-contain shrink-0", !isDarkMode && "brightness-0")}
                            draggable={false}
                          />
                          <span className={cn("text-[11px] font-light leading-normal", isDarkMode ? "text-white" : "text-gray-900")}>
                            {t('staticTranslations.specDetails.share', 'Share')}
                          </span>
                        </button>
                      </div>
                    </motion.div>

                    {/* Background sub-panel — slides in from the menu side */}
                    <AnimatePresence>
                      {isBackgroundSelectorOpen && (
                        <motion.div
                          initial={{ opacity: 0, x: isRTL ? -12 : 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: isRTL ? -12 : 12 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                          className={cn(
                            'rounded-[var(--brand-card-radius-sm)] overflow-hidden shadow-2xl flex-shrink-0 border',
                            isDarkMode
                              ? 'bg-[#1a1a1a] border-[#3a3a3a]'
                              : 'bg-[#f3f4f6] border-[#cacaca]',
                          )}
                          style={{
                            order: isRTL ? -1 : 1,
                            padding: '7px',
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                            {Object.entries(backgrounds).map(([id, bg]) => (
                              <Background key={id} backgroundId={id} onClick={handleBackgroundClick}>
                                <BgCard
                                  title={isRTL ? (bg.title?.ar ?? bg.title?.en ?? '') : (bg.title?.en ?? '')}
                                  image={bg.image}
                                  isDarkMode={isDarkMode}
                                  isRTL={isRTL}
                                  selected={id === backgroundId}
                                  compact
                                />
                              </Background>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* EXT/INT Camera Toggle — rightmost desktop control */}
            {!(activeTab === 'EXTERIOR' || activeTab === 'INTERIOR') && (
              <div
                className="group flex flex-col items-center gap-1"
                style={{ direction: 'ltr', ...(isRTL ? { marginRight: '15px' } : { marginLeft: '15px' }) }}
              >
                {/* Container: 93px wide × 53px tall (circle height) */}
                <div className="relative" style={{ width: '93px', height: '53px' }}>
                  {/* Blue gradient pill — 93×31px, vertically centered on circle */}
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      width: '93px', height: '31px', top: '11px', left: 0,
                      borderRadius: '50px',
                      backgroundImage: 'linear-gradient(152.85deg, var(--brand-toggle-gradient-from) 41.952%, var(--brand-toggle-gradient-to) 73.249%)',
                    }}
                  />
                  {/* "Int" text — right side of pill, visible when EXT is active */}
                  <span
                    className="absolute pointer-events-none text-[#f3f4f6] flex items-center justify-center transition-opacity duration-300"
                    style={{
                      left: '59px', top: '20px', width: '22px', height: '11px',
                      fontSize: '14px', fontFamily: "var(--bold-font-family)",
                      opacity: isInterior ? 0 : 1,
                    }}
                  >
                    {t('staticTranslations.cameraViewToggle.int', 'Int')}
                  </span>
                  {/* "Ext" text — left side of pill, visible when INT is active */}
                  <span
                    className="absolute pointer-events-none text-[#f3f4f6] flex items-center justify-center transition-opacity duration-300"
                    style={{
                      left: '12px', top: '20px', width: '22px', height: '11px',
                      fontSize: '14px', fontFamily: "var(--bold-font-family)",
                      opacity: isInterior ? 1 : 0,
                    }}
                  >
                    {t('staticTranslations.cameraViewToggle.ext', 'Ext')}
                  </span>
                  {/* Sliding white circle — 53×53px */}
                  <div
                    className="absolute rounded-full flex items-center justify-center pointer-events-none transition-all duration-300"
                    style={{
                      width: '53px', height: '53px', top: 0,
                      left: isInterior ? '40px' : '0px',
                      background: '#f3f4f6',
                      border: '5px solid var(--brand-toggle-knob-border)',
                      filter: 'drop-shadow(5px 0px 2px rgba(0,0,0,0.25))',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px', color: '#262626',
                        fontFamily: "var(--bold-font-family)", fontWeight: '500',
                      }}
                    >
                      {isInterior ? t('staticTranslations.cameraViewToggle.int', 'Int') : t('staticTranslations.cameraViewToggle.ext', 'Ext')}
                    </span>
                  </div>
                  {/* Clickable left half → EXT */}
                  <HotspotSwitch hotspotId="exteriorCamera" onClick={() => {
                    trackEvent(AnalyticsIds.viewToggleInteract, {
                      primaryTypeName: 'ext',
                      secondaryTypeName: view?.type,
                    });
                    resetToFreeRoam();
                  }}>
                    <div className="absolute left-0 inset-y-0 cursor-pointer z-20" style={{ width: '50%' }} />
                  </HotspotSwitch>
                  {/* Clickable right half → INT */}
                  <HotspotSwitch hotspotId="interiorCamera" onClick={() => {
                    trackEvent(AnalyticsIds.viewToggleInteract, {
                      primaryTypeName: 'int',
                      secondaryTypeName: view?.type,
                    });
                    resetToFreeRoam();
                  }}>
                    <div className="absolute right-0 inset-y-0 cursor-pointer z-20" style={{ width: '50%' }} />
                  </HotspotSwitch>
                </div>
                <span className={cn(
                  'text-[12px] font-medium uppercase tracking-wider transition-opacity opacity-0 group-hover:opacity-100',
                  isDarkMode ? 'text-white/40' : 'text-gray-400',
                )}>
                  {isInterior ? t('staticTranslations.cameraViewToggle.ext', 'Ext') : t('staticTranslations.cameraViewToggle.int', 'Int')}
                </span>
              </div>
            )}
          </div>

          {/* ── Mobile Background Selector Overlay ── */}
          {/* Figma: white card 209×85px, left:25px, above toolbar. Two bg cards 94×74px each */}
          <AnimatePresence>
            {isBackgroundSelectorOpen && !isMobileMenuOpen && (
              <motion.div
                ref={mobileBgRef}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className={cn("absolute bottom-[49px] z-[45] @3xl:hidden", isRTL ? "right-[45px]" : "left-[45px]")}
              >
                <div
                  className="bg-white rounded-[var(--brand-card-radius)] overflow-hidden"
                  style={{ width: '209px', padding: '5.5px 8px', border: '0.819px solid #cacaca' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
                    {Object.entries(backgrounds).map(([id, bg]) => (
                      <Background key={id} backgroundId={id} onClick={handleBackgroundClick}>
                        <BgCard
                          title={isRTL ? (bg.title?.ar ?? bg.title?.en ?? '') : (bg.title?.en ?? '')}
                          image={bg.image}
                          isDarkMode={false}
                          isRTL={isRTL}
                          selected={id === backgroundId}
                          mobile
                        />
                      </Background>
                    ))}
                  </div>
                </div>
                {/* Downward caret — points toward the menu button */}
                <div style={{ [isRTL ? 'paddingRight' : 'paddingLeft']: '96px', lineHeight: 0 }}>
                  <svg width="16" height="9" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 0L8 9L16 0Z" fill="white"/>
                  </svg>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

        </>
      )}

      {/* ── Mobile Bottom Toolbar — always visible including in preview/explore mode ── */}
      <div className="absolute bottom-0 inset-x-0 @3xl:hidden h-[45px] bg-[#262626] overflow-visible z-30" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="flex items-center justify-between h-full px-[15px]">

          {/* Left group: EXT/INT toggle + Interactions + Menu + Expand */}
          <div className="flex items-center gap-[14px]" style={isRTL ? { marginRight: '10px' } : undefined}>

                {/* EXT/INT Toggle pill */}
                <div className="relative flex-shrink-0" style={{ width: '58px', height: '33px' }}>
                    {/* Blue gradient pill */}
                    <div
                      className="absolute pointer-events-none"
                      style={{
                        width: '58px', height: '19px', top: '7px', left: 0,
                        borderRadius: 'var(--brand-pill-radius)',
                        backgroundImage: 'linear-gradient(152.85deg, var(--brand-toggle-gradient-from) 41.952%, var(--brand-toggle-gradient-to) 73.249%)',
                      }}
                    />
                    {/* "Int" text — right side, visible when EXT is active */}
                    <span
                      className="absolute pointer-events-none text-[#f3f4f6] flex items-center justify-center transition-opacity duration-300"
                      style={{
                        left: '36px', top: '11px', width: '14px', height: '12px',
                        fontSize: '8.7px', fontFamily: "var(--bold-font-family)",
                        opacity: isInterior ? 0 : 1,
                      }}
                    >
                      {t('staticTranslations.cameraViewToggle.int', 'Int')}
                    </span>
                    {/* "Ext" text — left side, visible when INT is active */}
                    <span
                      className="absolute pointer-events-none text-[#f3f4f6] flex items-center justify-center transition-opacity duration-300"
                      style={{
                        left: '7px', top: '11px', width: '14px', height: '12px',
                        fontSize: '8.7px', fontFamily: "var(--bold-font-family)",
                        opacity: isInterior ? 1 : 0,
                      }}
                    >
                      {t('staticTranslations.cameraViewToggle.ext', 'Ext')}
                    </span>
                    {/* Sliding white circle */}
                    <div
                      className="absolute rounded-full flex items-center justify-center pointer-events-none transition-all duration-300"
                      style={{
                        width: '33px', height: '33px', top: 0,
                        left: isInterior ? '25px' : '0px',
                        background: '#f3f4f6',
                        border: '3px solid var(--brand-toggle-knob-border)',
                        filter: 'drop-shadow(3px 0px 1.2px rgba(0,0,0,0.25))',
                      }}
                    >
                      <span style={{ fontSize: '8.7px', color: '#262626', fontFamily: "var(--bold-font-family)", fontWeight: '500' }}>
                        {isInterior ? t('staticTranslations.cameraViewToggle.int', 'Int') : t('staticTranslations.cameraViewToggle.ext', 'Ext')}
                      </span>
                    </div>
                    {/* Clickable halves */}
                    <HotspotSwitch hotspotId="exteriorCamera" onClick={() => {
                      trackEvent(AnalyticsIds.viewToggleInteract, {
                        primaryTypeName: 'ext',
                        secondaryTypeName: view?.type,
                      });
                      resetToFreeRoam();
                    }}>
                      <div className="absolute left-0 inset-y-0 cursor-pointer z-20" style={{ width: '50%' }} />
                    </HotspotSwitch>
                    <HotspotSwitch hotspotId="interiorCamera" onClick={() => {
                      trackEvent(AnalyticsIds.viewToggleInteract, {
                        primaryTypeName: 'int',
                        secondaryTypeName: view?.type,
                      });
                      resetToFreeRoam();
                    }}>
                      <div className="absolute right-0 inset-y-0 cursor-pointer z-20" style={{ width: '50%' }} />
                    </HotspotSwitch>
                  </div>

                {/* Interactions button */}
                <div ref={mobileInteractionsRef} className="relative flex-shrink-0">
                  <button
                    ref={mobileInteractionsBtnRef}
                    onClick={() => { resetToFreeRoam(); setIsMobileInteractionsOpen((p) => !p); }}
                    className={cn(
                      'rounded-full flex items-center justify-center transition-all flex-shrink-0',
                      isMobileInteractionsOpen ? 'bg-[var(--brand-accent)]' : 'bg-[#f3f4f6]',
                    )}
                    style={{ width: '32px', height: '32px' }}
                  >
                    <img
                      src={interactionsIcon}
                      alt="interactions"
                      className={cn('w-[16px] h-[16px]', isMobileInteractionsOpen ? 'invert' : 'brightness-0')}
                    />
                  </button>
                  {/* Interactions overlay — centered above this button via CSS */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-[8px] z-[45]">
                    <InteractionsPanel
                      isOpen={isMobileInteractionsOpen}
                      onClose={() => setIsMobileInteractionsOpen(false)}
                      isDarkMode={isDarkMode}
                      mobile
                    />
                  </div>
                </div>

                {/* Menu button */}
                <div ref={mobileMenuRef} className="relative flex-shrink-0">
                  {/* Outer div always has white border; bg changes to blue when open */}
                  <div
                    className={cn(
                      'rounded-full border border-solid border-white transition-colors',
                      (isMobileMenuOpen || isBackgroundSelectorOpen) && 'bg-[var(--brand-accent)]',
                    )}
                  >
                    <button
                      onClick={() => {
                        resetToFreeRoam();
                        trackEvent(AnalyticsIds.menuInteract, {
                          state: !(isMobileMenuOpen || isBackgroundSelectorOpen),
                          primaryTypeName: 'trayCta',
                        });
                        if (isMobileMenuOpen || isBackgroundSelectorOpen) { setIsMobileMenuOpen(false); setIsBackgroundSelectorOpen(false); }
                        else { setIsMobileMenuOpen(true); }
                      }}
                      className={cn(
                        'rounded-full flex items-center justify-center transition-all',
                        (isMobileMenuOpen || isBackgroundSelectorOpen) ? 'bg-[var(--brand-accent)] text-white' : 'bg-[#f3f4f6] text-[#262626]',
                      )}
                      style={{ width: '32px', height: '32px' }}
                    >
                      <Menu className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Menu popup — opens upward (Figma compact white card) */}
                  <AnimatePresence>
                    {isMobileMenuOpen && (
                      <div
                        className="absolute bottom-full left-1/2 -translate-x-1/2 flex flex-col items-center z-[100]"
                      >
                        {/* Compact white card + caret */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: 4 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: 4 }}
                          transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                          className="flex-shrink-0"
                        >
                          <div
                            className="bg-white rounded-[var(--brand-button-radius)] shadow-[0_2px_12px_rgba(0,0,0,0.18)] border border-white/20"
                            // Grow to fit the longest label (e.g. Cadillac's wider brand
                            // font overflowed "Switch Language (عربي)" at a fixed 131px),
                            // never narrower than the original 131px design width.
                            style={{ minWidth: '131px', width: 'max-content' }}
                          >
                            <div className="flex flex-col gap-[4px] p-[3.7px]">
                              {/* Change Background */}
                              <button
                                onClick={() => {
                                  trackEvent(AnalyticsIds.backgroundInteract, {
                                    state: true,
                                    parent: 'menu',
                                    primaryTypeName: 'background',
                                  });
                                  setIsMobileMenuOpen(false);
                                  setIsBackgroundSelectorOpen(true);
                                }}
                                className="flex items-center gap-[9.5px] h-[22px] px-[9px] rounded-[var(--brand-button-radius)] w-full transition-colors"
                                style={{
                                  background: isBackgroundSelectorOpen ? 'var(--brand-accent)' : '#e4e4e4',
                                  border: '0.5px solid rgba(102,102,102,0.5)',
                                }}
                              >
                                <img
                                  src={`${assetUrl}icons/homepage/mountain.svg`}
                                  alt=""
                                  className={cn('w-[15px] h-[8px] object-contain shrink-0', !isBackgroundSelectorOpen && 'brightness-0')}
                                  draggable={false}
                                />
                                <span className={cn('text-[8px] font-light leading-normal whitespace-nowrap', isBackgroundSelectorOpen ? 'text-white' : 'text-[#101010]')}>
                                  {t('staticTranslations.viewport.changeBackground', 'Change Background')}
                                </span>
                              </button>
                              {/* Select Language */}
                              <button
                                onClick={() => {
                                  const newLanguage = currentLanguage === 'en' ? 'ar' : 'en';
                                  trackEvent(AnalyticsIds.languageInteract, {
                                    parent: 'menu',
                                    primaryTypeName: newLanguage,
                                    secondaryTypeName: currentLanguage,
                                  });
                                  setLanguage(newLanguage);
                                  setIsMobileMenuOpen(false);
                                  setIsBackgroundSelectorOpen(false);
                                }}
                                className="flex items-center gap-[9.5px] h-[22px] px-[9px] rounded-[var(--brand-button-radius)] w-full bg-[#e4e4e4] transition-colors"
                                style={{ border: '0.5px solid rgba(102,102,102,0.5)' }}
                              >
                                <img
                                  src={`${assetUrl}icons/homepage/translate.svg`}
                                  alt=""
                                  className="w-[10px] h-[10px] object-contain shrink-0 brightness-0"
                                  draggable={false}
                                />
                                <span className="text-[8px] font-light leading-normal whitespace-nowrap text-[#101010]">
                                  {t('staticTranslations.viewport.selectLanguage', 'Switch Language')}
                                </span>
                              </button>
                              {/* Share */}
                              <button
                                onClick={() => { setIsMobileMenuOpen(false); setIsBackgroundSelectorOpen(false); onShare?.(); }}
                                className="flex items-center gap-[9.5px] h-[22px] px-[9px] rounded-[var(--brand-button-radius)] w-full bg-[#e4e4e4] transition-colors"
                                style={{ border: '0.5px solid rgba(102,102,102,0.5)' }}
                              >
                                <img
                                  src={`${assetUrl}icons/homepage/share.svg`}
                                  alt=""
                                  className="w-[11px] h-[12px] object-contain shrink-0 brightness-0"
                                  draggable={false}
                                />
                                <span className="text-[8px] font-light leading-normal whitespace-nowrap text-[#101010]">
                                  {t('staticTranslations.specDetails.share', 'Share')}
                                </span>
                              </button>
                            </div>
                          </div>
                          {/* Downward caret — points toward the menu button below */}
                          <div className="flex justify-center">
                            <div
                              style={{
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: '7px solid white',
                                filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.12))',
                              }}
                            />
                          </div>
                        </motion.div>
                      </div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Expand / Fullscreen button */}
                {!isIOSDevice && (
                  <button
                    onClick={() => { resetToFreeRoam(); toggleFullscreen(); }}
                    className={cn(
                      'rounded-full flex items-center justify-center transition-all flex-shrink-0',
                      isExpanded ? 'bg-[var(--brand-accent)] text-white' : 'bg-[#f3f4f6] text-[#262626]',
                    )}
                    style={{ width: '32px', height: '32px' }}
                  >
                    {isExpanded ? <Shrink className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
                  </button>
                )}
              </div>

              {/* Right: Explore Features / Build & Price toggle — mobile phones only.
                  md:hidden: the moment the side Config Panel is showing (window ≥ md,
                  same threshold it appears at - App.tsx / ConfigPanel.tsx:1583) it
                  already has this exact toggle in its own header, so this copy would
                  just be a redundant duplicate on tablets. */}
              {isPreviewMode ? (
                <button
                  onClick={() => onBuildAndPrice?.()}
                  className="md:hidden bg-[var(--brand-accent)] border border-[var(--brand-button-border)] flex items-center justify-center gap-[5.458px] text-white uppercase font-bold tracking-[0.48px] rounded-[var(--brand-button-radius-sm)] flex-shrink-0"
                  style={{ height: '27px', minWidth: '152px', fontSize: '12px' }}
                >
                  <span>{t('staticTranslations.buildAndPrice', 'Build & Price')}</span>
                </button>
              ) : (
                <button
                  onClick={() => { resetToFreeRoam(); onExploreFeatures?.(); }}
                  className="md:hidden bg-[#313131] border border-[var(--brand-button-border)] flex items-center justify-center gap-[5.458px] text-white uppercase font-bold tracking-[0.48px] rounded-[var(--brand-button-radius-sm)] flex-shrink-0"
                  style={{ height: '27px', minWidth: '152px', fontSize: '12px' }}
                >
                  <span>{t('staticTranslations.viewport.exploreFeatures', 'Explore features')}</span>
                  <ExternalLink className="w-[13px] h-[13px] text-[var(--brand-accent)]" strokeWidth={1.5} />
                </button>
              )}

            </div>
          </div>
    </div>
  );
};
